import type { PersonSortKey, PersonStatus, PresenceState } from '@sentra/contracts';

/**
 * The register's cache keys, in the eagerly-loaded half beside the administration section's.
 *
 * Split out of `api/queries.ts` for the reason `api/admin-keys.ts` is split out of the
 * administration hooks: `api/invalidation.ts` has to name them, and it is reached from every write
 * in the product. Keeping a key registry small and free of hooks is what lets a write declare what
 * it changed without dragging a screen's worth of imports along with it.
 */

/** What the register asks the server for. Declared here because the key is built from every member. */
export interface PersonRegisterQuery {
  readonly term: string;
  readonly status?: PersonStatus;
  readonly country?: string;
  readonly presence?: PresenceState;
  readonly photo?: 'with' | 'without';
  readonly sort?: PersonSortKey;
  readonly direction?: 'asc' | 'desc';
  /** Which page to serve, 1-based. */
  readonly page: number;
  /** How many rows a page holds. The register's own constant, not the API's default. */
  readonly limit: number;
  /**
   * Whether to ask at all.
   *
   * The register's screen also renders the registration form and one person's record, and the hook
   * that reads this is called above both of those returns because a hook has to be. A page of real
   * names fetched behind a screen that never shows it is a request nobody asked for, on the one
   * flow where an operator has somebody standing in front of them.
   */
  readonly enabled: boolean;
}

/** The cache keys, in one place, so an invalidation cannot miss a query by misspelling it. */
export const queryKeys = {
  fieldDefinitions: () => ['field.list'] as const,
  /**
   * Every member of the request, because every one of them changes the answer.
   *
   * A key that named only the term would leave the library serving page one out of its cache for
   * every page, every filter and every ordering the operator asked for afterwards: a query function
   * whose closure changed is not a query it refetches.
   */
  personRegister: (query: PersonRegisterQuery) =>
    [
      'person.list',
      query.term,
      query.status ?? '',
      query.country ?? '',
      query.presence ?? '',
      query.photo ?? '',
      query.sort ?? '',
      query.direction ?? '',
      query.page,
      query.limit,
    ] as const,
  personDetail: (id: string) => ['person.get', id] as const,
  // In the registry rather than inline at its one reader, which is the whole point of this object:
  // the revisions list was spelled `['person.revisions', id]` at the query and nowhere else, so
  // nothing invalidated it and the History of changes still said one revision after a second write.
  personRevisions: (id: string) => ['person.revisions', id] as const,
  personImages: (id: string) => ['person.image_list', id] as const,
  // Here for the same reason the revisions key is: two screens read a person's passes now — the
  // record shows the list and the amendment screen issues into it — and the key was a literal
  // spelled out at the query and again at the invalidation, which is one rename away from a list
  // that never refreshes after a reissue.
  personCredentials: (id: string) => ['credential.list', id] as const,
  presenceCounts: () => ['presence.occupancy'] as const,
  /**
   * One photograph's thumbnail, kept for the desk and administration screens that draw it.
   *
   * **The retention class is part of the key**, which is the only way to keep it. TanStack keeps a
   * query for the LONGEST `gcTime` any observer ever gave it, so a gate observer asking for nothing
   * to be kept would be overruled by the register having asked for five minutes of the same image.
   * Two keys are two queries, and the gate's is removed the moment its screen lets go of it.
   */
  personThumbnail: (imageId: string) => ['person.thumbnail', 'desk', imageId] as const,
  /** The same thumbnail as the gate draws it: held while on screen and never after (`gate.tsx`). */
  gateThumbnail: (imageId: string) => ['person.thumbnail', 'gate', imageId] as const,
  /** Every thumbnail in both classes, for an erasure that must not leave a face in memory. */
  personThumbnailPrefix: () => ['person.thumbnail'] as const,
  duplicateCandidates: (fields: Readonly<Record<string, unknown>>, excludePersonId?: string) =>
    ['person.duplicate_candidates', JSON.stringify(fields), excludePersonId ?? ''] as const,

  /**
   * The three PREFIXES a write names when it does not know which row it changed.
   *
   * TanStack matches an invalidation by prefix, so `['person.list']` reaches every page, filter and
   * ordering the register has cached — which is what a write has to reach, because the key of the
   * page an operator happens to be looking at is not knowable from the route. They are builders
   * here rather than literals at each effect for the reason the whole registry exists: the
   * revisions list was once spelled at its query and again at its invalidation, and the two drifted
   * until nothing refreshed after a second write.
   */
  personRegisterPrefix: () => ['person.list'] as const,
  personDetailPrefix: () => ['person.get'] as const,
  personCredentialsPrefix: () => ['credential.list'] as const,
} as const;
